<template>
  <div>
    <div class="text-left layout-actions">
      <naj-action-button
        v-for="action in layoutActions"
        :key="action.id"
        light
        color="primary"
        :icon="action.icon"
        :tooltip-message="action.tooltip"
        :type="action.actionType"
        :url="action.url"
        @click-action="onActionClick(item, action.url, header.httpMethod)"
        @open-popup="openModal(action.url)"
      >
        {{ action.actionName }}
      </naj-action-button>
    </div>

    <form
      :model="searchQuery"
      @submit.prevent="onSearch"
    >
      <naj-box
        v-if="headers && headers.length"
        :title="local.common.search"
        has-footer
      >
        <div v-if="filters && filters.length">
          <v-row>
            <v-col
              v-for="filter in basicFilters"
              :key="filter.id"
              cols="12"
              lg="4"
            >
              <naj-input
                :type-id="filter.typeId"
                :label="filter.label"
                :value.sync="searchQuery[filter.name]"
                :items="filter.dataSource"
                :validates="filter.validations"
                item-text="name"
                item-value="id"
              ></naj-input>
            </v-col>
          </v-row>

          <v-row v-if="isAdvancedSearchExpanded">
            <v-col
              v-for="filter in advancedFilters"
              :key="filter.id"
              cols="12"
              lg="4"
            >
              <naj-input
                :type-id="filter.typeId"
                :label="filter.label"
                :value.sync="searchQuery[filter.name]"
                :items="filter.dataSource"
                :validates="filter.properties"
                item-text="name"
                item-value="id"
              ></naj-input>
            </v-col>
          </v-row>
        </div>

        <template v-slot:footer>
          <h5
            v-if="advancedFilters.length"
            class="advanced-search-btn"
            @click="isAdvancedSearchExpanded = !isAdvancedSearchExpanded"
          >
            {{
              isAdvancedSearchExpanded
                ? local.dataGrid.hideAdvancedSearch
                : local.dataGrid.showAdvancedSearch
            }}
            <v-icon
              class="icon"
              :class="{ up: isAdvancedSearchExpanded }"
            >
              mdi-chevron-down
            </v-icon>
          </h5>
          <span v-else />
          <naj-button
            outlined
            type="submit"
            color="primary"
            icon="mdi-magnify"
          >
            {{ local.common.search }}
          </naj-button>
        </template>
      </naj-box>
    </form>

    <naj-data-table
      ref="selection"
      :headers="headers"
      :loading="tableLoading"
      :items="items"
      :disable-items-per-page="true"
      :server-items-length="total"
      :pagination="true"
      :show-select="showGridSelect"
      :title="local.common.searchResults"
      :items-per-page="paging.pageSize"
      :page-index="paging.pageIndex"
      :skip-casting-enabled="skipCastingEnabled"
      :skip-casting-keys="skipCastingKeys"
      @update:pageIndex="onPageChange"
      @input="selectionChange"
    >
      <template v-slot:header>
        <div class="grid-actions">
          <naj-action-button
            v-for="action in gridActions"
            :key="action.id"
            outlined
            color="primary"
            :icon="action.icon"
            :tooltip-message="action.tooltip"
            :disabled="isGridActionDisabled(action)"
            :type="action.actionType"
            :url="
              action.actionType == actionType.gridPrinting ? gridPrintingUrl(action) : action.url
            "
            @click-action="onActionClick(item, action.url, header.httpMethod)"
            @open-popup="openModal(action.url, action.properties.params)"
          >
            {{ action.actionName }}
          </naj-action-button>
        </div>
      </template>
      <template slot-scope="{ item, header }">
        <naj-action-button
          v-if="header && item[header.actionValue]"
          outlined
          size="small"
          :type="header.slot"
          :url="item[header.actionValue]"
          @click-action="onActionClick(item, item[header.actionValue], header.httpMethod)"
          @open-popup="openModal(item[header.actionValue], item)"
        >
          {{ executeString(header.actionName, item) }}
        </naj-action-button>
      </template>
    </naj-data-table>

    <redistribution-modal
      :modal-visibility="modal.show == 'redistribution-modal'"
      :params="modal.params"
      :selected-requests="this.selectedRows"
      @onClose="closeModal"
      @successSubmit="deselectAllAndUpdate"
    ></redistribution-modal>
    <services-center-redistribution-modal
      :modal-visibility="modal.show == 'services-center-redistribution-modal'"
      :selected-requests="this.selectedRows"
      @onClose="closeModal"
      @successSubmit="deselectAllAndUpdate"
    ></services-center-redistribution-modal>
    <change-path-modal
      :modal-visibility="modal.show == 'change-path-modal'"
      :selected-requests="this.selectedRows"
      @onClose="closeModal"
      @successSubmit="deselectAllAndUpdate"
    ></change-path-modal>
    <request-records-modal
      :modal-visibility="modal.show == 'request-records-modal'"
      :request-object-key="(modal.params || {}).objectKey"
      @onClose="closeModal"
    ></request-records-modal>
  </div>
</template>

<script>
import { modalPosition, actionType, httpMethod } from '@/helpers/enum';
import { executeString } from '@/helpers/utils';
import RedistributionModal from '@/components/redistribution-modal';
import RequestRecordsModal from '@/modules/lawsuit/components/request-records-modal/index';
import ServicesCenterRedistributionModal from '@/components/services-center-redistribution-modal';
import ChangePathModal from '@/components/change-path-modal';
import NajBox from '@/components/shared/naj-box';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('GlobalModule');

export default {
  name: 'DataGrid',
  components: {
    RedistributionModal,
    ServicesCenterRedistributionModal,
    ChangePathModal,
    RequestRecordsModal,
    NajBox,
  },
  data() {
    return {
      executeString,
      selectedRows: [],
      searchQuery: {},
      modalPosition,
      actionType,
      httpMethod,
      width: '1200',
      modal: {
        show: '',
        params: {},
      },
      tableLoading: false,
      isAdvancedSearchExpanded: false,
    };
  },
  computed: {
    ...mapState({
      items: state => state.dataGrid.items,
      total: state => state.dataGrid.total,
      filters: state => state.dataGrid.filters,
      actions: state => state.dataGrid.actions,
      headers: state => state.dataGrid.columns,
      paging: state => state.dataGrid.searchQuery.paging,
      skipCastingEnabled: state => state.dataGrid.skipCastingEnabled,
      skipCastingKeys: state => state.dataGrid.skipCastingKeys,
    }),
    ...mapGetters(['currentPageTitle', 'activeMenuItem', 'isAutoSearch']),
    basicFilters() {
      return this.filters.filter(f => f.isBasic);
    },
    advancedFilters() {
      return this.filters.filter(f => !f.isBasic);
    },
    // these computed props needs to be enhanced
    // backend should specify the type.
    layoutActions() {
      return this.actions.filter(action => action.actionType === actionType.addNew);
    },
    gridActions() {
      return this.actions.filter(action => action.actionType !== actionType.addNew);
    },
    showGridSelect() {
      const actionsRequireSelection = this.gridActions.filter(
        a => a.actionType !== actionType.gridPrinting
      );
      return !!actionsRequireSelection.length;
    },
  },
  methods: {
    ...mapActions([
      'getDataGridConfigurations',
      'getDataGridItems',
      'sortChange',
      'pageChange',
      'pageSizeChange',
      'searchChange',
    ]),
    openModal(componentName, params) {
      this.modal.show = componentName;
      this.modal.params = params || {};
    },
    closeModal() {
      this.modal.show = '';
      setTimeout(() => {
        this.modal.params = {};
      }, 250);
    },
    selectionChange(selections) {
      this.selectedRows = selections;
    },
    deselectAll() {
      this.$refs.selection.toggleSelectAll(false);
    },
    onSortChange(searchQuery) {
      this.sortChange(searchQuery);
      this.updateDataGridItems();
    },
    onPageChange(pageIndex) {
      this.pageChange(pageIndex);
      this.updateDataGridItems();
    },
    onPageSizeChange(pageSize) {
      this.pageSizeChange(pageSize);
      this.updateDataGridItems();
    },
    onSearch() {
      this.$validator.validateAll()
        .then((valid) => {
          if (valid) {
            this.searchChange(this.searchQuery);
            this.updateDataGridItems();
          }
        });
    },
    gridPrintingUrl(action) {
      return {
        path: action.url,
        query: {
          total: this.total,
          title: this.currentPageTitle,
          gridId: this.activeMenuItem.gridId,
          searchQuery: JSON.stringify(this.searchQuery),
        },
      };
    },
    isGridActionDisabled(action) {
      return (
        (action.actionType !== actionType.gridPrinting ||
          (action.actionType === actionType.gridPrinting && !this.total)) &&
        !(this.selectedRows && this.selectedRows.length)
      );
    },
    deselectAllAndUpdate() {
      this.deselectAll();
      this.updateDataGridItems();
    },
    updateDataGridItems() {
      this.tableLoading = true;
      this.getDataGridItems()
        .finally(() => (this.tableLoading = false));
    },
  },
  watch: {
    activeMenuItem() {
      this.searchQuery = {};
      this.searchChange(this.searchQuery);
      this.isAdvancedSearchExpanded = false;
      this.getDataGridConfigurations()
        .then(() => {
          this.isAutoSearch && this.updateDataGridItems();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .ivu-table-header th {
  background-color: #eee !important;
}
.layout-actions {
  position: absolute;
  top: 19px;
  left: 50px;
}
.grid-actions {
  display: inline-flex;
}
.icon {
  margin-right: 15px;
}
.up {
  -webkit-transform: rotate(180deg);
  -moz-transform: rotate(180deg);
  -o-transform: rotate(180deg);
  transform: rotate(180deg);
  // -webkit-transition-duration: 1s;
  // -moz-transition-duration: 1s;
  // -o-transition-duration: 1s;
  // transition-duration: 1s;
}
.advanced-search-btn {
  font-size: 20px;
  line-height: 23px;
  font-weight: 400;
  color: #252733;
  cursor: pointer;
}
::v-deep .v-data-table-header {
  border-top: none !important;
}
::v-deep .v-data-table tbody tr:hover {
  background-color: #fcfcfc !important;
}
</style>
